import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Camera,
  CheckCircle2,
  ExternalLink,
  Eye,
  Globe,
  ImageIcon,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Star,
  AlertCircle,
  Pin,
  PlusCircle,
} from 'lucide-react';
import {
  Alert,
  Button,
  Panel,
  PanelBody,
  SectionLabel,
} from '../ui/index.js';
import { cn } from '../ui/cn.js';
import { fetchJson, ApiError } from '../lib/fetch-json.js';
import { SubBrandPills } from '../components/SubBrandPills.js';
import { canonicalParentBrand, KNOWN_SUB_BRANDS, normalizeName } from '@mapara/core';

const MIN_IMAGE_PX = 1000;

interface BrandItem {
  readonly name: string;
  readonly count: number;
}

export interface BrandSiteConfig {
  readonly brandKey: string;
  readonly sourceKey: string;
  readonly websiteUrl: string;
  readonly label: string;
  readonly placeholder: string;
}

export const BRAND_CONFIGS: readonly BrandSiteConfig[] = [
  {
    brandKey: 'caudalie',
    sourceKey: 'caudalie-official',
    websiteUrl: 'https://fr.caudalie.com/',
    label: 'fr.caudalie.com',
    placeholder: 'https://fr.caudalie.com/p/...',
  },
  {
    brandKey: 'avene',
    sourceKey: 'avene-official',
    websiteUrl: 'https://www.eau-thermale-avene.fr/',
    label: 'eau-thermale-avene.fr',
    placeholder: 'https://www.eau-thermale-avene.fr/p/...',
  },
  {
    brandKey: 'aderma',
    sourceKey: 'aderma-official',
    websiteUrl: 'https://www.aderma.fr/',
    label: 'aderma.fr',
    placeholder: 'https://www.aderma.fr/fr-fr/p/...',
  },
  {
    brandKey: 'ducray',
    sourceKey: 'ducray-official',
    websiteUrl: 'https://www.ducray.com/',
    label: 'ducray.com',
    placeholder: 'https://www.ducray.com/fr-fr/p/...',
  },
  {
    brandKey: 'klorane',
    sourceKey: 'klorane-official',
    websiteUrl: 'https://www.klorane.com/',
    label: 'klorane.com',
    placeholder: 'https://www.klorane.com/fr-fr/p/...',
  },
  {
    brandKey: 'nuxe',
    sourceKey: 'nuxe-official',
    websiteUrl: 'https://fr.nuxe.com/',
    label: 'fr.nuxe.com',
    placeholder: 'https://fr.nuxe.com/products/...',
  },
  {
    brandKey: 'cavailles',
    sourceKey: 'cavailles-official',
    websiteUrl: 'https://www.cavaillès.fr/',
    label: 'cavaillès.fr',
    placeholder: 'https://www.cavaillès.fr/produits/...',
  },
  {
    brandKey: 'bioderma',
    sourceKey: 'bioderma-official',
    websiteUrl: 'https://www.bioderma.fr/',
    label: 'bioderma.fr',
    placeholder: 'https://www.bioderma.fr/p/...',
  },
  {
    brandKey: 'svr',
    sourceKey: 'svr-official',
    websiteUrl: 'https://fr.svr.com/',
    label: 'fr.svr.com',
    placeholder: 'https://fr.svr.com/products/...',
  },
  {
    brandKey: 'larocheposay',
    sourceKey: 'larocheposay-official',
    websiteUrl: 'https://www.laroche-posay.fr/',
    label: 'laroche-posay.fr',
    placeholder: 'https://www.laroche-posay.fr/gammes/...',
  },
  {
    brandKey: 'cerave',
    sourceKey: 'cerave-official',
    websiteUrl: 'https://www.cerave.fr/',
    label: 'cerave.fr',
    placeholder: 'https://www.cerave.fr/nos-produits/...',
  },
  {
    brandKey: 'uriage',
    sourceKey: 'uriage-official',
    websiteUrl: 'https://www.uriage.fr/',
    label: 'uriage.fr',
    placeholder: 'https://www.uriage.fr/produits/...',
  },
  {
    brandKey: 'vichy',
    sourceKey: 'vichy-official',
    websiteUrl: 'https://www.vichy.fr/',
    label: 'vichy.fr',
    placeholder: 'https://www.vichy.fr/soins/...',
  },
  {
    brandKey: 'mustela',
    sourceKey: 'mustela-official',
    websiteUrl: 'https://www.mustela.com/',
    label: 'mustela.com',
    placeholder: 'https://www.mustela.com/products/...',
  },
  {
    brandKey: 'phyto',
    sourceKey: 'phyto-official',
    websiteUrl: 'https://fr.phyto.com/',
    label: 'fr.phyto.com',
    placeholder: 'https://fr.phyto.com/p/...',
  },
  {
    brandKey: 'renefurterer',
    sourceKey: 'renefurterer-official',
    websiteUrl: 'https://www.renefurterer.com/',
    label: 'renefurterer.com',
    placeholder: 'https://www.renefurterer.com/fr-fr/p/...',
  },
  {
    brandKey: 'chicco',
    sourceKey: 'chicco-official',
    websiteUrl: 'https://www.chicco.ch/fr/',
    label: 'chicco.ch',
    placeholder: 'https://www.chicco.ch/fr/produits/...',
  },
];

function cleanBrandKey(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Maps any brand or sub-brand (e.g. "Bioderma", "Atoderm", "Eau Thermale Avène", "Cleanance")
 * to its calibrated official brand website configuration.
 */
export function getBrandConfig(brand: string | null | undefined): BrandSiteConfig | null {
  if (!brand || brand.trim() === '' || brand.toLowerCase() === 'all') return null;

  // 1. Direct key match (e.g. 'bioderma', 'svr', 'avene')
  const cleanInput = cleanBrandKey(brand);
  const directMatch = BRAND_CONFIGS.find(
    (c) => cleanInput.includes(c.brandKey) || c.brandKey.includes(cleanInput),
  );
  if (directMatch) return directMatch;

  // 2. Canonical parent brand (e.g. 'Eau Thermale Avène' -> 'avene')
  const canonical = canonicalParentBrand(brand);
  const cleanCanonical = cleanBrandKey(canonical);
  const canonicalMatch = BRAND_CONFIGS.find(
    (c) => cleanCanonical.includes(c.brandKey) || c.brandKey.includes(cleanCanonical),
  );
  if (canonicalMatch) return canonicalMatch;

  // 3. Sub-brand lookup (e.g. 'Atoderm' -> 'bioderma', 'Cleanance' -> 'avene')
  const norm = normalizeName(brand);
  const parent = KNOWN_SUB_BRANDS[norm];
  if (parent) {
    const cleanParent = cleanBrandKey(parent);
    const parentMatch = BRAND_CONFIGS.find(
      (c) => cleanParent.includes(c.brandKey) || c.brandKey.includes(cleanParent),
    );
    if (parentMatch) return parentMatch;
  }

  return null;
}

/**
 * Resolves the official brand site configuration.
 * Always prioritizes the currently selected main brand on the page (e.g. "BIODERMA"),
 * followed by the product's brand/sub-brand and product name tokens.
 */
export function resolveBrandConfig(
  productBrand: string | null | undefined,
  pageBrand: string | null | undefined,
  productName?: string | null | undefined,
): BrandSiteConfig | null {
  // 1. Primary: if the user explicitly selected a main brand in the dropdown, follow it!
  const pageCfg = getBrandConfig(pageBrand);
  if (pageCfg) return pageCfg;

  // 2. Secondary: product's brand field (which may be parent brand or sub-brand)
  const prodCfg = getBrandConfig(productBrand);
  if (prodCfg) return prodCfg;

  // 3. Fallback: scan product name for brand tokens
  if (productName) {
    const cleanName = cleanBrandKey(productName);
    for (const c of BRAND_CONFIGS) {
      if (cleanName.includes(c.brandKey)) return c;
    }
  }

  return null;
}

function normalizeWooSideloadUrl(url: string): string {
  let u = url.trim();
  if (u.includes('wedia-group.com') || u.includes('media-pierre-fabre')) {
    u = u.split('?')[0] ?? u;
    // DO NOT strip /pf_square/ — keeping pf_square guarantees 1620x1620 square packshots without narrow cropping.
    const parts = u.split('/');
    const last = parts[parts.length - 1] ?? '';
    const secondLast = parts[parts.length - 2] ?? '';
    if (last.replace(/\.jpe?g$/i, '') === secondLast) {
      parts[parts.length - 1] = `${secondLast}.png`;
      u = parts.join('/');
    } else if (!/\.(jpe?g|png|webp)$/i.test(u)) {
      u = `${u}.png`;
    }
    return u;
  }
  return u;
}

interface CatalogProduct {
  readonly id: number;
  readonly sku: string | null;
  readonly name: string;
  readonly brand: string | null;
  readonly images: readonly string[];
  readonly imageCount: number;
  readonly gtinCanonical: string | null;
  readonly regularPriceTtc: number | null;
  readonly salePriceTtc: number | null;
  readonly wcProductId: number;
  readonly isDone?: boolean;
  readonly doneAt?: string | null;
  readonly doneBy?: string | null;
  readonly doneSourceUrl?: string | null;
}

export type PublishMode = 'replace_all' | 'keep_main' | 'append_gallery';

export interface FinalImagePreview {
  readonly url: string;
  readonly isMain: boolean;
  readonly origin: 'site' | 'new';
  readonly label: string;
}

export function computeFinalImages(
  mode: PublishMode,
  existingImages: readonly string[],
  mainImage: ScrapedImageItem | undefined,
  scrapedImages: readonly ScrapedImageItem[],
): readonly FinalImagePreview[] {
  const result: FinalImagePreview[] = [];
  const seen = new Set<string>();

  const selectedGallery = scrapedImages
    .filter((img) => img.inGallery && img.id !== mainImage?.id)
    .sort((a, b) => a.galleryRank - b.galleryRank);

  if (mode === 'append_gallery' && existingImages.length > 0) {
    existingImages.forEach((url, idx) => {
      const norm = normalizeWooSideloadUrl(url);
      if (!seen.has(norm)) {
        seen.add(norm);
        result.push({
          url: norm,
          isMain: idx === 0,
          origin: 'site',
          label: idx === 0 ? 'Site (Principale)' : `Site (#${idx + 1})`,
        });
      }
    });
    if (mainImage) {
      const norm = normalizeWooSideloadUrl(mainImage.url);
      if (!seen.has(norm)) {
        seen.add(norm);
        result.push({
          url: norm,
          isMain: false,
          origin: 'new',
          label: `Nouvelle (#${result.length + 1})`,
        });
      }
    }
    for (const g of selectedGallery) {
      const norm = normalizeWooSideloadUrl(g.url);
      if (!seen.has(norm)) {
        seen.add(norm);
        result.push({
          url: norm,
          isMain: false,
          origin: 'new',
          label: `Nouvelle (#${result.length + 1})`,
        });
      }
    }
  } else if (mode === 'keep_main' && existingImages.length > 0) {
    const normMain = normalizeWooSideloadUrl(existingImages[0]!);
    seen.add(normMain);
    result.push({
      url: normMain,
      isMain: true,
      origin: 'site',
      label: 'Site (Principale)',
    });
    if (mainImage) {
      const norm = normalizeWooSideloadUrl(mainImage.url);
      if (!seen.has(norm)) {
        seen.add(norm);
        result.push({
          url: norm,
          isMain: false,
          origin: 'new',
          label: `Nouvelle (#${result.length + 1})`,
        });
      }
    }
    for (const g of selectedGallery) {
      const norm = normalizeWooSideloadUrl(g.url);
      if (!seen.has(norm)) {
        seen.add(norm);
        result.push({
          url: norm,
          isMain: false,
          origin: 'new',
          label: `Nouvelle (#${result.length + 1})`,
        });
      }
    }
  } else {
    // replace_all
    if (mainImage) {
      const norm = normalizeWooSideloadUrl(mainImage.url);
      seen.add(norm);
      result.push({
        url: norm,
        isMain: true,
        origin: 'new',
        label: 'Nouvelle (Principale)',
      });
    }
    for (const g of selectedGallery) {
      const norm = normalizeWooSideloadUrl(g.url);
      if (!seen.has(norm)) {
        seen.add(norm);
        result.push({
          url: norm,
          isMain: false,
          origin: 'new',
          label: `Nouvelle (#${result.length + 1})`,
        });
      }
    }
  }

  return result;
}

interface ScrapedImageItem {
  readonly id: string;
  readonly url: string;
  isMain: boolean;
  inGallery: boolean;
  galleryRank: number;
}

export function ImageStudioSection(): React.ReactElement {
  const queryClient = useQueryClient();

  // Step 1: Selected brand and brand filter
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [brandSearch, setBrandSearch] = useState<string>('');
  const [excludedSubBrands, setExcludedSubBrands] = useState<string[]>([]);

  // Step 2: Products list filters
  const [productSearch, setProductSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'todo' | 'done' | 'no_images'>('all');

  // Step 3: Selected product
  const [selectedProduct, setSelectedProduct] = useState<CatalogProduct | null>(null);

  // Step 4: Official website matching & scraping
  const [officialUrl, setOfficialUrl] = useState<string>('');

  // Step 5: Scraped images with dimensions and selection
  const [scrapedImages, setScrapedImages] = useState<ScrapedImageItem[]>([]);
  const [imageDims, setImageDims] = useState<Record<string, { w: number; h: number } | 'error'>>({});

  // Step 6: Publication mode and confirmation
  const [publishMode, setPublishMode] = useState<PublishMode>('replace_all');
  const [confirmArm, setConfirmArm] = useState<boolean>(false);
  const [banner, setBanner] = useState<{ ok: boolean; text: ReactNode } | null>(null);

  // Load brands roster
  const brandsQuery = useQuery({
    queryKey: ['image-studio-brands'],
    queryFn: () => fetchJson<{ brands: readonly BrandItem[] }>('/api/v1/image-studio/brands'),
  });

  const brands = brandsQuery.data?.brands ?? [];

  const [searchParams, setSearchParams] = useSearchParams();
  const urlBrand = searchParams.get('brand') ?? '';
  const savedBrand = typeof window !== 'undefined' ? (sessionStorage.getItem('mapara:image-studio:brand') ?? '') : '';

  // Default brand selection once loaded
  useEffect(() => {
    if (selectedBrand === '' && brands.length > 0) {
      const matchUrl = urlBrand ? brands.find((b) => b.name.toLowerCase() === urlBrand.toLowerCase()) : null;
      const matchSaved = savedBrand ? brands.find((b) => b.name.toLowerCase() === savedBrand.toLowerCase()) : null;
      const initial = matchUrl?.name ?? matchSaved?.name ?? brands[0]!.name;
      setSelectedBrand(initial);
      setExcludedSubBrands([]);
    }
  }, [brands, selectedBrand, urlBrand, savedBrand]);

  // Load products of the selected brand
  const productsQuery = useQuery({
    queryKey: ['image-studio-products', selectedBrand, productSearch, statusFilter, excludedSubBrands],
    queryFn: () => {
      const q = new URLSearchParams();
      if (selectedBrand) q.set('brand', selectedBrand);
      if (excludedSubBrands.length > 0) q.set('excludedSubBrands', excludedSubBrands.join(','));
      if (productSearch.trim()) q.set('q', productSearch.trim());
      if (statusFilter !== 'all') q.set('filter', statusFilter);
      q.set('pageSize', '100');
      return fetchJson<{ total: number; doneTotal?: number; items: readonly CatalogProduct[] }>(
        `/api/v1/image-studio/products?${q.toString()}`,
      );
    },
    enabled: selectedBrand !== '',
  });

  const products = productsQuery.data?.items ?? [];
  const doneTotal = productsQuery.data?.doneTotal ?? 0;
  const currentBrandItem = brands.find((b) => b.name === selectedBrand);
  const currentBrandCount = currentBrandItem?.count ?? 0;
  const donePct = currentBrandCount > 0 ? Math.round((doneTotal / currentBrandCount) * 100) : 0;

  // When selected product changes, reset scraping state
  useEffect(() => {
    setScrapedImages([]);
    setImageDims({});
    setConfirmArm(false);
    setPublishMode('replace_all');
    setOfficialUrl(selectedProduct?.doneSourceUrl ?? '');

    // Auto-trigger brand official search if product has no saved URL and brand is calibrated
    if (selectedProduct && !selectedProduct.doneSourceUrl) {
      const cfg = resolveBrandConfig(selectedProduct.brand, selectedBrand, selectedProduct.name);
      if (cfg) {
        void handleAutoFindBrand(selectedProduct, cfg);
      }
    }
  }, [selectedProduct?.id]);

  // Measure natural dimensions of images in browser
  useEffect(() => {
    let cancelled = false;
    for (const img of scrapedImages) {
      if (imageDims[img.url] !== undefined) continue;
      const probe = new Image();
      probe.onload = () => {
        if (cancelled) return;
        setImageDims((prev) => ({
          ...prev,
          [img.url]: { w: probe.naturalWidth, h: probe.naturalHeight },
        }));
      };
      probe.onerror = () => {
        if (cancelled) return;
        setImageDims((prev) => ({ ...prev, [img.url]: 'error' }));
      };
      probe.src = img.url;
    }
    return () => {
      cancelled = true;
    };
  }, [scrapedImages, imageDims]);

  // Mutation: Scrape direct URL
  const scrapeUrlMutation = useMutation({
    mutationFn: (url: string) =>
      fetchJson<{ ok: boolean; sourceUrl: string; images: string[]; count: number }>(
        '/api/v1/image-studio/scrape-url',
        { method: 'POST', body: JSON.stringify({ url, productId: selectedProduct?.id }) },
      ),
    onSuccess: (res) => {
      const initial: ScrapedImageItem[] = res.images.map(normalizeWooSideloadUrl).map((url, idx) => ({
        id: `img-${idx}-${Date.now()}`,
        url,
        isMain: idx === 0,
        inGallery: true,
        galleryRank: idx + 1,
      }));
      setScrapedImages(initial);
      setBanner({
        ok: true,
        text: `✓ ${res.images.length} photo(s) haute résolution récupérée(s) depuis le site officiel.`,
      });
    },
    onError: (err: Error) => {
      setBanner({
        ok: false,
        text: `Échec de récupération : ${err.message}`,
      });
    },
  });

  // Helper: auto-find for calibrated brands (Caudalie, Avène...)
  const handleAutoFindBrand = async (prod: CatalogProduct, cfg: BrandSiteConfig) => {
    try {
      setBanner({ ok: true, text: `Recherche automatique sur ${cfg.label}…` });
      const res = await fetchJson<{
        ok: boolean;
        sourceUrl?: string;
        gallery?: string[];
        reason?: string;
        detail?: string;
        count?: number;
        sizeNote?: string;
      }>('/api/v1/image-studio/live-fetch', {
        method: 'POST',
        body: JSON.stringify({ productId: prod.id, sourceKey: cfg.sourceKey }),
      });

      if (res.ok && res.gallery && res.gallery.length > 0 && res.sourceUrl) {
        setOfficialUrl(res.sourceUrl);
        const initial: ScrapedImageItem[] = res.gallery.map(normalizeWooSideloadUrl).map((url, idx) => ({
          id: `img-${idx}-${Date.now()}`,
          url,
          isMain: idx === 0,
          inGallery: true,
          galleryRank: idx + 1,
        }));
        setScrapedImages(initial);
        setBanner({
          ok: true,
          text: `✓ Galerie officielle ${cfg.label} trouvée : ${res.gallery.length} photos prêtes à être vérifiées.${
            res.sizeNote ? ` Format : ${res.sizeNote}.` : ''
          }`,
        });
        return;
      }

      // A size the official page does not carry is a hard no — tell the
      // operator which formats exist instead of pushing a wrong packshot.
      if (res.reason === 'size_mismatch') {
        setBanner({
          ok: false,
          text: `Formats incompatibles sur ${cfg.label} : ${res.detail ?? 'la page officielle ne couvre pas ce format'}. Vérifiez la contenance du produit ou collez directement l'URL officielle ci-dessous.`,
        });
        return;
      }

      // Fallback query if live-fetch did not return gallery directly
      if (res.ok) {
        const sets = await fetchJson<{ items: readonly { sourceUrl: string; gallery: string[] }[] }>(
          `/api/v1/image-studio?status=proposed&brand=${encodeURIComponent(cfg.brandKey)}`,
        );
        const match = sets.items.find((s) => s.gallery.length > 0);
        if (match) {
          setOfficialUrl(match.sourceUrl);
          const initial: ScrapedImageItem[] = match.gallery.map(normalizeWooSideloadUrl).map((url, idx) => ({
            id: `img-${idx}-${Date.now()}`,
            url,
            isMain: idx === 0,
            inGallery: true,
            galleryRank: idx + 1,
          }));
          setScrapedImages(initial);
          setBanner({
            ok: true,
            text: `✓ Galerie officielle ${cfg.label} trouvée : ${match.gallery.length} photos prêtes à être vérifiées.`,
          });
          return;
        }
      }

      setBanner({
        ok: false,
        text: `Aucune correspondance automatique trouvée sur ${cfg.label}. Vous pouvez coller directement l'URL officielle du produit ci-dessous.`,
      });
    } catch (e) {
      setBanner({
        ok: false,
        text: `Recherche automatique impossible : ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  };

  // Mutation: Push to WooCommerce
  const pushMutation = useMutation({
    mutationFn: (payload: {
      productId: number;
      mainImageUrl: string;
      galleryImageUrls: string[];
      mode?: PublishMode;
      sourceUrl?: string;
    }) =>
      fetchJson<{ ok: boolean; batchId: number; verified: number; images: string[]; mode?: PublishMode }>(
        '/api/v1/image-studio/push-images',
        { method: 'POST', body: JSON.stringify(payload) },
      ),
    onSuccess: (res) => {
      setConfirmArm(false);
      setBanner({
        ok: true,
        text: `✓ ${res.images.length} photo(s) envoyée(s) et synchronisées sur votre boutique WooCommerce pour ${selectedProduct?.name} !`,
      });
      // Invalidate products query to refresh current images in the list
      void queryClient.invalidateQueries({ queryKey: ['image-studio-products'] });
      // Update selected product locally
      if (selectedProduct) {
        setSelectedProduct({
          ...selectedProduct,
          images: res.images,
          imageCount: res.images.length,
          isDone: true,
          doneAt: new Date().toISOString(),
          doneBy: 'operator',
          doneSourceUrl: officialUrl.trim() || (selectedProduct.doneSourceUrl ?? null),
        });
      }
    },
    onError: (err) => {
      setConfirmArm(false);
      const detail =
        err instanceof ApiError ? err.data.detail ?? err.data.error ?? err.message : err.message;
      setBanner({ ok: false, text: `Échec de l'envoi sur WooCommerce : ${detail}` });
    },
  });

  // Mutation: Sync directly from WooCommerce
  const syncFromWooMutation = useMutation({
    mutationFn: (productId: number) =>
      fetchJson<{ ok: boolean; productId: number; images: string[]; count: number; isDone: boolean }>(
        `/api/v1/image-studio/${productId}/sync-from-woo`,
        { method: 'POST' },
      ),
    onSuccess: (res) => {
      setBanner({
        ok: true,
        text: `✓ ${res.count} photo(s) synchronisée(s) depuis WooCommerce pour ${selectedProduct?.name} !`,
      });
      void queryClient.invalidateQueries({ queryKey: ['image-studio-products'] });
      if (selectedProduct && selectedProduct.id === res.productId) {
        setSelectedProduct({
          ...selectedProduct,
          images: res.images,
          imageCount: res.count,
          isDone: res.isDone,
          doneAt: new Date().toISOString(),
          doneBy: 'WooCommerce Live',
        });
      }
    },
    onError: (err) => {
      const detail = err instanceof ApiError ? err.data.detail ?? err.data.error ?? err.message : err.message;
      setBanner({ ok: false, text: `Échec de la synchronisation depuis WooCommerce : ${detail}` });
    },
  });

  const handleSetMain = (id: string) => {
    setScrapedImages((prev) =>
      prev.map((img) => ({
        ...img,
        isMain: img.id === id,
      })),
    );
  };

  const handleToggleGallery = (id: string) => {
    setScrapedImages((prev) => {
      const target = prev.find((img) => img.id === id);
      if (!target) return prev;
      const nextInGallery = !target.inGallery;
      const updated = prev.map((img) => (img.id === id ? { ...img, inGallery: nextInGallery } : img));
      // Recompute ranks
      let rank = 1;
      return updated.map((img) => {
        if (img.inGallery) {
          const r = rank++;
          return { ...img, galleryRank: r };
        }
        return { ...img, galleryRank: 0 };
      });
    });
  };

  const handleSelectAll = () => {
    let rank = 1;
    setScrapedImages((prev) =>
      prev.map((img) => ({
        ...img,
        inGallery: true,
        galleryRank: rank++,
      })),
    );
  };

  const handleDeselectAll = () => {
    setScrapedImages((prev) =>
      prev.map((img) => ({
        ...img,
        inGallery: img.isMain,
        galleryRank: img.isMain ? 1 : 0,
      })),
    );
  };

  const handleExecutePush = () => {
    if (!selectedProduct) return;
    const main = scrapedImages.find((img) => img.isMain) ?? scrapedImages[0];
    const hasSiteImages = selectedProduct.images.length > 0;

    if (publishMode === 'replace_all' || !hasSiteImages) {
      if (!main) {
        alert('Veuillez sélectionner au moins une photo à publier.');
        return;
      }
    }

    const gallery = scrapedImages
      .filter((img) => img.inGallery && img.id !== main?.id)
      .sort((a, b) => a.galleryRank - b.galleryRank)
      .map((img) => normalizeWooSideloadUrl(img.url));

    pushMutation.mutate({
      productId: selectedProduct.id,
      mainImageUrl: main ? normalizeWooSideloadUrl(main.url) : '',
      galleryImageUrls: gallery,
      mode: publishMode,
      ...(officialUrl.trim() ? { sourceUrl: officialUrl.trim() } : {}),
    });
  };

  // Filtered brands for brand dropdown
  const filteredBrands = brands.filter((b) =>
    b.name.toLowerCase().includes(brandSearch.trim().toLowerCase()),
  );

  const mainImage = scrapedImages.find((img) => img.isMain) ?? scrapedImages[0];
  const galleryCount = scrapedImages.filter((img) => img.inGallery && img.id !== mainImage?.id).length;
  const totalSelectedCount = mainImage ? 1 + galleryCount : 0;
  const finalStack = selectedProduct
    ? computeFinalImages(publishMode, selectedProduct.images, mainImage, scrapedImages)
    : [];

  return (
    <div className="space-y-4">
      {/* Notifications banner */}
      {banner && (
        <Alert
          tone={banner.ok ? 'good' : 'warn'}
          title={banner.ok ? 'Succès' : 'Information'}
          actions={
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="text-xs text-ink-soft hover:text-ink font-semibold"
            >
              Fermer
            </button>
          }
        >
          {banner.text}
        </Alert>
      )}

      {/* STEP 1: Select Brand from our website */}
      <Panel>
        <PanelBody className="space-y-3">
          <SectionLabel>1 · Choisir une marque de votre catalogue</SectionLabel>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-72">
              <label htmlFor="brand-select" className="block text-xs font-bold text-ink mb-1">
                Marque ({brands.length} marques disponibles dans votre catalogue)
              </label>
              <div className="flex gap-2">
                <select
                  id="brand-select"
                  value={selectedBrand}
                  onChange={(e) => {
                    const newBrand = e.target.value;
                    setSelectedBrand(newBrand);
                    setExcludedSubBrands([]);
                    setSelectedProduct(null);
                    setOfficialUrl('');
                    setScrapedImages([]);
                    try {
                      sessionStorage.setItem('mapara:image-studio:brand', newBrand);
                      setSearchParams(
                        (prev) => {
                          const next = new URLSearchParams(prev);
                          next.set('brand', newBrand);
                          return next;
                        },
                        { replace: true },
                      );
                    } catch {
                      // ignore
                    }
                  }}
                  className="h-11 flex-1 rounded-lg border border-border bg-card px-3 text-sm font-semibold text-ink shadow-xs"
                >
                  {filteredBrands.map((b) => (
                    <option key={b.name} value={b.name}>
                      {b.name} ({b.count} produits)
                    </option>
                  ))}
                </select>
                <div className="relative w-44">
                  <Search
                    className="absolute left-2.5 top-3.5 h-4 w-4 text-ink-ghost pointer-events-none"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    value={brandSearch}
                    onChange={(e) => setBrandSearch(e.target.value)}
                    placeholder="Filtrer marque…"
                    className="h-11 w-full rounded-lg border border-border bg-card pl-8 pr-2.5 text-xs text-ink shadow-xs"
                  />
                </div>
              </div>
            </div>

            {/* Sub-brands & ranges checkbox pills */}
            <div className="w-full">
              <SubBrandPills
                brand={selectedBrand}
                excludedSubBrands={excludedSubBrands}
                onChange={setExcludedSubBrands}
              />
            </div>

            {/* Quick Filter: all vs todo vs done vs missing photos */}
            <div className="flex flex-wrap items-end gap-2 pt-5">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={cn(
                  'h-11 rounded-lg px-3 text-xs font-bold border transition-colors',
                  statusFilter === 'all'
                    ? 'border-sky-500 bg-sky-50 text-sky-900 shadow-xs'
                    : 'border-border bg-card text-ink-soft hover:bg-canvas',
                )}
              >
                Tous ({currentBrandCount})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('todo')}
                className={cn(
                  'h-11 rounded-lg px-3 text-xs font-bold border transition-colors',
                  statusFilter === 'todo'
                    ? 'border-amber-500 bg-amber-50 text-amber-900 shadow-xs'
                    : 'border-border bg-card text-ink-soft hover:bg-canvas',
                )}
              >
                À faire ({Math.max(0, currentBrandCount - doneTotal)})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('done')}
                className={cn(
                  'h-11 rounded-lg px-3 text-xs font-bold border transition-colors flex items-center gap-1.5',
                  statusFilter === 'done'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-900 shadow-xs'
                    : 'border-border bg-card text-ink-soft hover:bg-canvas',
                )}
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                Déjà faits ({doneTotal})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('no_images')}
                className={cn(
                  'h-11 rounded-lg px-3 text-xs font-bold border transition-colors flex items-center gap-1.5',
                  statusFilter === 'no_images'
                    ? 'border-rose-500 bg-rose-50 text-rose-900 shadow-xs'
                    : 'border-border bg-card text-ink-soft hover:bg-canvas',
                )}
              >
                <Camera className="h-3.5 w-3.5 text-rose-600" />
                Sans photo
              </button>
            </div>
          </div>

          {currentBrandCount > 0 && (
            <div className="w-full flex items-center justify-between text-xs pt-2.5 border-t border-border/60">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-ink-soft">Progression pour « {selectedBrand} » :</span>
                <span className="font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full text-[11px]">
                  {doneTotal} / {currentBrandCount} mis à jour ({donePct} %)
                </span>
              </div>
              <div className="hidden sm:block w-40 bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200">
                <div
                  className="bg-emerald-500 h-full rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, Math.max(0, donePct))}%` }}
                />
              </div>
            </div>
          )}
        </PanelBody>
      </Panel>

      {/* Main 2-Column Workflow Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT COLUMN: List of products of the selected brand (Step 2 & 3) */}
        <div className="lg:col-span-5 space-y-3">
          <Panel>
            <PanelBody className="p-3 space-y-2">
              <div className="flex items-center justify-between pb-1">
                <SectionLabel>2 · Produits {selectedBrand ? `de « ${selectedBrand} »` : ''}</SectionLabel>
                <span className="text-xs font-semibold text-ink-soft">
                  {products.length} affiché(s)
                </span>
              </div>

              {/* Search within brand */}
              <div className="relative">
                <Search
                  className="absolute left-3 top-2.5 h-4 w-4 text-ink-ghost pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Rechercher nom, SKU ou code-barres…"
                  className="h-9 w-full rounded-md border border-border bg-card pl-9 pr-3 text-xs text-ink placeholder:text-ink-soft"
                />
              </div>

              {/* Products list */}
              <div className="max-h-[680px] overflow-y-auto space-y-1.5 pr-1 pt-1">
                {productsQuery.isPending ? (
                  <div className="py-8 text-center text-xs text-ink-soft flex items-center justify-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin" /> Chargement des produits…
                  </div>
                ) : products.length === 0 ? (
                  <div className="py-8 text-center text-xs text-ink-soft">
                    Aucun produit trouvé pour ce filtre.
                  </div>
                ) : (
                  products.map((p) => {
                    const isSelected = selectedProduct?.id === p.id;
                    const hasPhotos = p.imageCount > 0;
                    const firstImg = p.images[0];

                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedProduct(p)}
                        className={cn(
                          'w-full text-left p-2.5 rounded-lg border transition-all flex items-center gap-3',
                          isSelected
                            ? 'border-sky-500 bg-sky-50/60 shadow-sm ring-1 ring-sky-500'
                            : 'border-border bg-card hover:border-slate-300 hover:bg-canvas/50',
                        )}
                      >
                        {/* Current thumbnail */}
                        <div className="relative h-14 w-14 shrink-0 rounded-md border border-border bg-white flex items-center justify-center overflow-hidden">
                          {firstImg ? (
                            <img
                              src={firstImg}
                              alt={p.name}
                              className="h-full w-full object-contain p-0.5"
                              loading="lazy"
                            />
                          ) : (
                            <ImageIcon className="h-6 w-6 text-slate-300" />
                          )}
                          {p.isDone && (
                            <span
                              className="absolute top-0.5 left-0.5 bg-emerald-600 text-white rounded-full p-0.5 shadow-xs"
                              title="Déjà mis à jour via Image Studio"
                            >
                              <CheckCircle2 className="h-3 w-3" />
                            </span>
                          )}
                          <span
                            className={cn(
                              'absolute -bottom-1 -right-1 px-1 rounded text-[10px] font-bold',
                              hasPhotos
                                ? 'bg-slate-800 text-white'
                                : 'bg-rose-600 text-white',
                            )}
                          >
                            {p.imageCount}
                          </span>
                        </div>

                        {/* Product meta */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-ink truncate leading-tight">
                            {p.name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="font-mono text-[11px] text-ink-soft font-semibold">
                              SKU: {p.sku ?? '—'}
                            </span>
                            {p.gtinCanonical && (
                              <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1 rounded">
                                {p.gtinCanonical}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Status badges */}
                        <div className="shrink-0 flex flex-col items-end gap-1 text-right">
                          {p.isDone ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-xs">
                              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                              ✓ Fait
                            </span>
                          ) : (
                            <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                              À faire
                            </span>
                          )}
                          <span
                            className={cn(
                              'inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold',
                              hasPhotos
                                ? 'bg-slate-100 text-slate-700'
                                : 'bg-rose-50 text-rose-800 border border-rose-200',
                            )}
                          >
                            {hasPhotos ? `${p.imageCount} photo(s)` : '0 photo'}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </PanelBody>
          </Panel>
        </div>

        {/* RIGHT COLUMN: Workspace for selected product (Step 3, 4, 5 & 6) */}
        <div className="lg:col-span-7 space-y-3">
          {selectedProduct === null ? (
            <Panel>
              <PanelBody className="py-16 text-center">
                <Camera className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <h3 className="text-sm font-bold text-ink">3 · Choisissez un produit à mettre à jour</h3>
                <p className="text-xs text-ink-soft max-w-sm mx-auto mt-1">
                  Cliquez sur un produit dans la liste à gauche pour afficher ses photos actuelles,
                  récupérer les visuels haute définition du site officiel et sélectionner celles à
                  publier.
                </p>
              </PanelBody>
            </Panel>
          ) : (
            <>
              {/* Product Card Header */}
              <Panel>
                <PanelBody className="space-y-3">
                  {/* Done info banner if already processed */}
                  {selectedProduct.isDone && (
                    <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-2.5 flex items-center justify-between text-xs text-emerald-950 shadow-xs">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                        <div>
                          <p className="font-bold">
                            ✓ Produit déjà mis à jour via Image Studio
                          </p>
                          <p className="text-[11px] text-emerald-800">
                            {selectedProduct.doneAt && (
                              <span>
                                Mis à jour le {new Date(selectedProduct.doneAt).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            )}
                            {selectedProduct.doneBy && (
                              <span className="ml-1.5 font-semibold">
                                ({selectedProduct.doneBy})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      {selectedProduct.doneSourceUrl && (
                        <a
                          href={selectedProduct.doneSourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] font-bold text-emerald-800 hover:text-emerald-950 underline flex items-center gap-1 shrink-0"
                        >
                          Source officielle <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-2 border-b border-border pb-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-sky-800 bg-sky-100 px-1.5 py-0.5 rounded">
                          {selectedBrand && selectedProduct.brand && selectedBrand.toLowerCase() !== selectedProduct.brand.toLowerCase()
                            ? `${selectedBrand} · ${selectedProduct.brand}`
                            : (selectedProduct.brand || selectedBrand || 'Sans marque')}
                        </span>
                        {selectedProduct.isDone ? (
                          <span className="text-[11px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                            Déjà fait
                          </span>
                        ) : (
                          <span className="text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                            À faire
                          </span>
                        )}
                        <span className="font-mono text-xs font-semibold text-ink-soft">
                          SKU : {selectedProduct.sku ?? '—'}
                        </span>
                        {selectedProduct.gtinCanonical && (
                          <span className="font-mono text-xs text-ink-soft">
                            EAN : {selectedProduct.gtinCanonical}
                          </span>
                        )}
                      </div>
                      <h2 className="text-sm font-bold text-ink mt-1">{selectedProduct.name}</h2>
                    </div>

                    <a
                      href={`https://maparatunisie.tn/?post_type=product&p=${selectedProduct.wcProductId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 flex items-center gap-1 text-xs text-sky-800 font-semibold hover:underline"
                    >
                      Voir sur boutique <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>

                  {/* Current Photos in WooCommerce */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                      <p className="text-xs font-bold text-ink flex items-center gap-1.5">
                        <ImageIcon className="h-3.5 w-3.5 text-ink-soft" />
                        Photos actuelles sur votre site ({selectedProduct.images.length}) :
                      </p>
                      <button
                        type="button"
                        onClick={() => syncFromWooMutation.mutate(selectedProduct.id)}
                        disabled={syncFromWooMutation.isPending}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800 transition-colors cursor-pointer shadow-2xs"
                        title="Vérifier et synchroniser les photos déjà publiées sur WooCommerce"
                      >
                        <RefreshCw className={cn('h-3.5 w-3.5 text-sky-600', syncFromWooMutation.isPending && 'animate-spin')} />
                        {syncFromWooMutation.isPending ? 'Lecture WooCommerce...' : 'Synchroniser depuis WooCommerce'}
                      </button>
                    </div>
                    {selectedProduct.images.length === 0 ? (
                      <p className="text-xs text-rose-600 font-semibold italic">
                        Aucune photo actuellement en boutique pour ce produit.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct.images.map((img, idx) => (
                          <div
                            key={idx}
                            className="relative h-16 w-16 rounded border border-border bg-white p-1 overflow-hidden"
                          >
                            <img
                              src={img}
                              alt=""
                              className="h-full w-full object-contain"
                              loading="lazy"
                            />
                            {idx === 0 && (
                              <span className="absolute top-0.5 left-0.5 bg-amber-500 text-white text-[9px] font-bold px-1 rounded">
                                Main
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </PanelBody>
              </Panel>

              {/* STEP 4: Scrape official website */}
              {(() => {
                const brandConfig = resolveBrandConfig(
                  selectedProduct.brand,
                  selectedBrand,
                  selectedProduct.name,
                );
                return (
                  <Panel>
                    <PanelBody className="space-y-3">
                      <SectionLabel>4 · Récupérer les photos du site officiel</SectionLabel>
                      <p className="text-xs text-ink-soft">
                        {brandConfig ? (
                          <>
                            Site officiel configuré pour <strong>{brandConfig.label}</strong> :{' '}
                            <a
                              href={brandConfig.websiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-bold text-sky-800 hover:underline inline-flex items-center gap-0.5"
                            >
                              {brandConfig.label} <ExternalLink className="h-3 w-3" />
                            </a>. Collez l'URL de la fiche produit ci-dessous :
                          </>
                        ) : (
                          <>
                            Collez l'URL de la fiche produit sur le site officiel de la marque :
                          </>
                        )}
                      </p>

                      <div className="flex flex-wrap items-center gap-2">
                        <div className="relative flex-1 min-w-72">
                          <Globe
                            className="absolute left-3 top-3.5 h-4 w-4 text-ink-ghost pointer-events-none"
                            aria-hidden="true"
                          />
                          <input
                            type="url"
                            value={officialUrl}
                            onChange={(e) => setOfficialUrl(e.target.value)}
                            placeholder={
                              brandConfig?.placeholder ??
                              'https://www.site-officiel.com/produit/...'
                            }
                            className="h-11 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-xs text-ink font-mono placeholder:text-ink-soft"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="primary"
                          size="md"
                          disabled={scrapeUrlMutation.isPending || officialUrl.trim() === ''}
                          onClick={() => scrapeUrlMutation.mutate(officialUrl.trim())}
                        >
                          {scrapeUrlMutation.isPending ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4" />
                          )}
                          {scrapeUrlMutation.isPending ? 'Récupération…' : 'Récupérer les photos'}
                        </Button>
                      </div>

                      {brandConfig && (
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          <a
                            href={brandConfig.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-sky-800 font-bold hover:underline inline-flex items-center gap-1 bg-sky-50 border border-sky-200 px-2.5 py-1 rounded-md"
                          >
                            <Globe className="h-3.5 w-3.5" />
                            Ouvrir {brandConfig.label} <ExternalLink className="h-3 w-3" />
                          </a>
                          <button
                            type="button"
                            onClick={() => void handleAutoFindBrand(selectedProduct, brandConfig)}
                            className="text-xs text-ink-soft hover:text-ink font-semibold inline-flex items-center gap-1 px-2 py-1"
                          >
                            <RefreshCw className="h-3 w-3" /> Relancer la recherche automatique {brandConfig.label}
                          </button>
                        </div>
                      )}
                    </PanelBody>
                  </Panel>
                );
              })()}

              {/* STEP 5: Scraped Photos Grid with Dimensions & Selection */}
              {scrapedImages.length > 0 && (
                <Panel>
                  <PanelBody className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2.5">
                      <div>
                        <SectionLabel>5 · Choisir les photos à publier</SectionLabel>
                        <p className="text-xs text-ink-soft mt-0.5">
                          {totalSelectedCount} photo(s) sélectionnée(s) ({mainImage ? '1 principale' : '0'} + {galleryCount} galerie).
                          Les dimensions sont mesurées en direct.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSelectAll}
                          className="text-xs font-bold text-sky-800 hover:underline px-2 py-1"
                        >
                          Tout cocher
                        </button>
                        <span className="text-ink-ghost">·</span>
                        <button
                          type="button"
                          onClick={handleDeselectAll}
                          className="text-xs font-bold text-ink-soft hover:underline px-2 py-1"
                        >
                          Garder principale seule
                        </button>
                      </div>
                    </div>

                    {/* Image Cards Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {scrapedImages.map((img) => {
                        const dim = imageDims[img.url];
                        const isMain = img.isMain;
                        const inGallery = img.inGallery;
                        const pass = dim && dim !== 'error' && dim.w >= MIN_IMAGE_PX && dim.h >= MIN_IMAGE_PX;

                        return (
                          <div
                            key={img.id}
                            className={cn(
                              'relative rounded-xl border p-2 bg-white flex flex-col justify-between transition-all',
                              isMain
                                ? 'border-amber-500 ring-2 ring-amber-400/50 shadow-sm'
                                : inGallery
                                  ? 'border-sky-500 ring-1 ring-sky-400/40 shadow-xs'
                                  : 'border-slate-200 opacity-60 hover:opacity-100',
                            )}
                          >
                            {/* Visual Preview */}
                            <div className="relative aspect-square w-full rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden mb-2">
                              <img
                                src={img.url}
                                alt=""
                                className="h-full w-full object-contain p-1"
                                loading="lazy"
                              />

                              {/* External Zoom Button */}
                              <a
                                href={img.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="absolute top-1.5 right-1.5 h-6 w-6 rounded bg-slate-900/60 text-white flex items-center justify-center hover:bg-slate-900"
                                title="Ouvrir l'image originale"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </a>

                              {/* Main image badge */}
                              {isMain && (
                                <span className="absolute top-1.5 left-1.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-xs flex items-center gap-1">
                                  <Star className="h-3 w-3 fill-white" /> Principale
                                </span>
                              )}
                            </div>

                            {/* Measured Dimensions Badge */}
                            <div className="mb-2">
                              {dim === undefined ? (
                                <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-ink-ghost">
                                  mesure…
                                </span>
                              ) : dim === 'error' ? (
                                <span className="inline-block rounded-full bg-rose-100 border border-rose-300 px-2 py-0.5 font-mono text-[10px] font-bold text-rose-800">
                                  inaccessible
                                </span>
                              ) : (
                                <span
                                  className={cn(
                                    'inline-block rounded-full px-2 py-0.5 font-mono text-[10px] font-bold border',
                                    pass
                                      ? 'bg-emerald-100 border-emerald-300 text-emerald-900'
                                      : 'bg-rose-100 border-rose-300 text-rose-800',
                                  )}
                                >
                                  {dim.w} × {dim.h} {pass ? '✓' : `< ${MIN_IMAGE_PX}px`}
                                </span>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-1.5 pt-1 border-t border-slate-100">
                              <button
                                type="button"
                                onClick={() => handleSetMain(img.id)}
                                className={cn(
                                  'flex-1 text-[11px] font-bold py-1 px-1.5 rounded transition-colors flex items-center justify-center gap-1',
                                  isMain
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-slate-100 text-ink-soft hover:bg-amber-100 hover:text-amber-900',
                                )}
                              >
                                <Star className={cn('h-3 w-3', isMain && 'fill-white')} />
                                {isMain ? 'Principale' : 'Mettre en 1'}
                              </button>

                              {!isMain && (
                                <button
                                  type="button"
                                  onClick={() => handleToggleGallery(img.id)}
                                  className={cn(
                                    'text-[11px] font-bold py-1 px-2 rounded border transition-colors',
                                    inGallery
                                      ? 'bg-sky-600 text-white border-sky-600'
                                      : 'bg-slate-50 text-ink-soft border-slate-200 hover:bg-slate-100',
                                  )}
                                >
                                  {inGallery ? `✓ #${img.galleryRank}` : '+ Galerie'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* STEP 6: Mode de publication et aperçu du résultat sur WooCommerce */}
                    <div className="pt-4 border-t border-border mt-4 space-y-4">
                      <div>
                        <SectionLabel>6 · Mode de publication sur WooCommerce</SectionLabel>
                        <p className="text-xs text-ink-soft mt-0.5">
                          Choisissez comment appliquer les photos sélectionnées sur votre boutique en ligne.
                        </p>
                      </div>

                      {/* 3 Publication Modes */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                        {/* Mode 1: replace_all */}
                        <button
                          type="button"
                          onClick={() => setPublishMode('replace_all')}
                          className={cn(
                            'p-3 rounded-xl border text-left transition-all flex flex-col justify-between gap-2',
                            publishMode === 'replace_all'
                              ? 'border-emerald-600 bg-emerald-50/70 ring-2 ring-emerald-500/40 shadow-xs'
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50',
                          )}
                        >
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-ink flex items-center gap-1.5">
                                <RefreshCw className={cn('h-3.5 w-3.5', publishMode === 'replace_all' ? 'text-emerald-700' : 'text-ink-soft')} />
                                Remplacer tout
                              </span>
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                                Standard
                              </span>
                            </div>
                            <p className="text-[11px] text-ink-soft leading-snug">
                              Remplace la photo principale et toute la galerie par la nouvelle sélection.
                            </p>
                          </div>
                          <div className="pt-1.5 border-t border-slate-200/60 flex items-center justify-between text-[10px] font-semibold">
                            <span className={publishMode === 'replace_all' ? 'text-emerald-800' : 'text-ink-ghost'}>
                              {publishMode === 'replace_all' ? '● Sélectionné' : '○ Choisir'}
                            </span>
                            <span className="text-amber-700 font-mono">Écrase l'existant</span>
                          </div>
                        </button>

                        {/* Mode 2: keep_main */}
                        <button
                          type="button"
                          disabled={selectedProduct.images.length === 0}
                          onClick={() => setPublishMode('keep_main')}
                          className={cn(
                            'p-3 rounded-xl border text-left transition-all flex flex-col justify-between gap-2',
                            selectedProduct.images.length === 0
                              ? 'opacity-40 cursor-not-allowed bg-slate-50 border-slate-200'
                              : publishMode === 'keep_main'
                                ? 'border-sky-600 bg-sky-50/70 ring-2 ring-sky-500/40 shadow-xs'
                                : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50',
                          )}
                        >
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-ink flex items-center gap-1.5">
                                <Pin className={cn('h-3.5 w-3.5', publishMode === 'keep_main' ? 'text-sky-700' : 'text-ink-soft')} />
                                Garder la principale du site
                              </span>
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">
                                Recommandé
                              </span>
                            </div>
                            <p className="text-[11px] text-ink-soft leading-snug">
                              Conserve la photo 1 actuelle du site. Les photos sélectionnées deviennent la galerie.
                            </p>
                          </div>
                          <div className="pt-1.5 border-t border-slate-200/60 flex items-center justify-between text-[10px] font-semibold">
                            <span className={publishMode === 'keep_main' ? 'text-sky-800' : 'text-ink-ghost'}>
                              {publishMode === 'keep_main' ? '● Sélectionné' : '○ Choisir'}
                            </span>
                            <span className="text-sky-700 font-mono">Conserve photo 1</span>
                          </div>
                        </button>

                        {/* Mode 3: append_gallery */}
                        <button
                          type="button"
                          disabled={selectedProduct.images.length === 0}
                          onClick={() => setPublishMode('append_gallery')}
                          className={cn(
                            'p-3 rounded-xl border text-left transition-all flex flex-col justify-between gap-2',
                            selectedProduct.images.length === 0
                              ? 'opacity-40 cursor-not-allowed bg-slate-50 border-slate-200'
                              : publishMode === 'append_gallery'
                                ? 'border-indigo-600 bg-indigo-50/70 ring-2 ring-indigo-500/40 shadow-xs'
                                : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50',
                          )}
                        >
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-ink flex items-center gap-1.5">
                                <PlusCircle className={cn('h-3.5 w-3.5', publishMode === 'append_gallery' ? 'text-indigo-700' : 'text-ink-soft')} />
                                Ajouter à la galerie
                              </span>
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800">
                                Sans écraser
                              </span>
                            </div>
                            <p className="text-[11px] text-ink-soft leading-snug">
                              Conserve la principale ET toute la galerie actuelle. Ajoute les nouvelles photos à la suite.
                            </p>
                          </div>
                          <div className="pt-1.5 border-t border-slate-200/60 flex items-center justify-between text-[10px] font-semibold">
                            <span className={publishMode === 'append_gallery' ? 'text-indigo-800' : 'text-ink-ghost'}>
                              {publishMode === 'append_gallery' ? '● Sélectionné' : '○ Choisir'}
                            </span>
                            <span className="text-indigo-700 font-mono">0 suppression</span>
                          </div>
                        </button>
                      </div>

                      {/* Visual Stack Preview: What WooCommerce will receive */}
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                          <span className="font-bold text-ink flex items-center gap-1.5">
                            <Eye className="h-3.5 w-3.5 text-ink-soft" />
                            Aperçu du résultat sur WooCommerce :
                          </span>
                          <span className="font-mono text-[11px] font-bold text-ink-soft bg-white border border-border px-2 py-0.5 rounded">
                            {finalStack.length} photo(s) au total (
                            {finalStack.filter((f) => f.origin === 'site').length} site +{' '}
                            {finalStack.filter((f) => f.origin === 'new').length} nouvelles)
                          </span>
                        </div>

                        {finalStack.length === 0 ? (
                          <p className="text-xs text-rose-600 italic">
                            Aucune photo sélectionnée pour la publication.
                          </p>
                        ) : (
                          <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-1">
                            {finalStack.map((item, idx) => (
                              <div
                                key={`${item.url}-${idx}`}
                                className={cn(
                                  'relative shrink-0 w-20 rounded-lg border bg-white p-1 text-center shadow-2xs',
                                  item.isMain
                                    ? 'border-amber-500 ring-2 ring-amber-400/50'
                                    : 'border-slate-200',
                                )}
                              >
                                <div className="aspect-square w-full rounded bg-slate-50 flex items-center justify-center overflow-hidden mb-1">
                                  <img
                                    src={item.url}
                                    alt=""
                                    className="h-full w-full object-contain"
                                    loading="lazy"
                                  />
                                </div>
                                <div className="space-y-0.5">
                                  <span
                                    className={cn(
                                      'block text-[9px] font-bold px-1 py-0.5 rounded truncate',
                                      item.isMain
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-slate-100 text-slate-700',
                                    )}
                                  >
                                    {item.isMain ? '★ Principale' : `#${idx + 1}`}
                                  </span>
                                  <span
                                    className={cn(
                                      'block text-[8px] font-bold uppercase tracking-wider truncate',
                                      item.origin === 'site' ? 'text-sky-700' : 'text-emerald-700',
                                    )}
                                  >
                                    {item.origin === 'site' ? 'Photo du site' : 'Nouvelle'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Confirmation Arming / Trigger */}
                      <div>
                        {!confirmArm ? (
                          <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 rounded-xl border border-border shadow-xs">
                            <div>
                              <p className="text-xs font-bold text-ink">
                                Prêt à synchroniser sur WooCommerce ?
                              </p>
                              <p className="text-[11px] text-ink-soft">
                                {publishMode === 'replace_all' &&
                                  'Mode Remplacer tout : les photos actuelles seront remplacées.'}
                                {publishMode === 'keep_main' &&
                                  'Mode Garder la principale : la photo 1 du site reste intacte.'}
                                {publishMode === 'append_gallery' &&
                                  'Mode Ajouter à la galerie : aucune photo existante ne sera supprimée.'}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="primary"
                              size="md"
                              disabled={pushMutation.isPending || finalStack.length === 0}
                              onClick={() => setConfirmArm(true)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                            >
                              <Send className="h-4 w-4" />
                              Envoyer sur WooCommerce ({finalStack.length})
                            </Button>
                          </div>
                        ) : (
                          <div className="bg-amber-50 border border-amber-300 p-3.5 rounded-xl space-y-2.5">
                            <p className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                              <AlertCircle className="h-4 w-4 text-amber-700" />
                              Confirmer la mise à jour pour « {selectedProduct.name} » ?
                            </p>
                            <p className="text-xs text-amber-800">
                              {publishMode === 'replace_all' && (
                                <>
                                  Toutes les photos actuelles du site seront remplacées par les{' '}
                                  <strong>{finalStack.length}</strong> nouvelles photos sélectionnées.
                                </>
                              )}
                              {publishMode === 'keep_main' && (
                                <>
                                  La <strong>photo principale actuelle du site</strong> sera conservée en position 1.
                                  Les <strong>{finalStack.length - 1}</strong> photos sélectionnées formeront la galerie.
                                </>
                              )}
                              {publishMode === 'append_gallery' && (
                                <>
                                  Toutes les photos actuelles du site seront <strong>conservées</strong>. Les{' '}
                                  <strong>
                                    {finalStack.filter((f) => f.origin === 'new').length}
                                  </strong>{' '}
                                  nouvelles photos sélectionnées seront ajoutées à la fin de la galerie.
                                </>
                              )}
                              {' '}Les balises ALT et titres seront automatiquement synchronisés.
                            </p>
                            <div className="flex items-center gap-2 pt-1">
                              <Button
                                type="button"
                                variant="primary"
                                size="md"
                                disabled={pushMutation.isPending}
                                onClick={handleExecutePush}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                              >
                                {pushMutation.isPending ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="h-4 w-4" />
                                )}
                                {pushMutation.isPending
                                  ? 'Envoi en cours…'
                                  : `Oui, publier (${finalStack.length} photos)`}
                              </Button>
                              <Button
                                type="button"
                                variant="soft"
                                size="md"
                                disabled={pushMutation.isPending}
                                onClick={() => setConfirmArm(false)}
                              >
                                Annuler
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </PanelBody>
                </Panel>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
